import { Alert, Box, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { Form, useTranslate } from 'react-admin';

import { EMPLOYEES_BULK_CREATE_FILE_DATA_STARTING_LINE } from '@Common/constants/b2b/employees';
import { PostEmployeesBulkCreateValidateEndpoint } from '@Common/types/bo/b2b/employees/bulk_create';

import { Button, DialogTitleWithX, SaveButton } from '@sh4res/bo-common/components';
import { DetailsRowGrid } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/DetailsRowGrid';
import { useOverwriteHighlightStyles } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/highlightStyles';
import { hasIgnoredOverrides } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/utils/hasIgnoredOverrides';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

interface Props {
  data: PostEmployeesBulkCreateValidateEndpoint['Client']['ResponseBody'] & {
    type: 'overwrite_confirmation';
  };
  errorMessage: string | null;
  confirmOverwrite: () => void;
  cancel: () => void;
  close: () => void;
}

const testId = 'BoExternalB2bEmployeeBulkCreateOverwrites';

const OverwriteForm = (props: Props & { showMixedIgnoredInfo: boolean }) => {
  const translate = useTranslate();
  const styles = useOverwriteHighlightStyles();

  return (
    <DialogContent>
      {props.showMixedIgnoredInfo && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid={`${testId}_MixedIgnoredInfo`}>
          {translate(
            'resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.mixed_ignored_info',
          )}
        </Alert>
      )}
      <Stack direction="row" spacing={2} paddingTop={1}>
        <Box>
          <Typography variant="subtitle2" color="#4580FF">
            <span style={{ fontWeight: 700 }}>
              {translate(
                'resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.subtitle',
              )}
              :
            </span>{' '}
            {Object.keys(props.data.overrides).length} value(s)
          </Typography>
          <Typography variant="body2">
            {translate(
              'resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.description',
            )}
          </Typography>
          {props.errorMessage && <Typography color="error.main">{props.errorMessage}</Typography>}
        </Box>
        <Box maxWidth={'75%'}>
          <DetailsRowGrid
            action="update"
            rawData={props.data.raw_data}
            shouldRenderRow={(idx) =>
              idx in props.data.overrides || idx in props.data.ignored_overrides
            }
            rowNumberForIndex={(idx) => idx + EMPLOYEES_BULK_CREATE_FILE_DATA_STARTING_LINE}
            info={(idx) => props.data.overrides[idx]}
            infoIgnored={(idx) => props.data.ignored_overrides[idx]}
            highlightRowClassName={styles.highlightedRow}
            highlightCellClassName={styles.highlightedCell}
          />
        </Box>
      </Stack>
    </DialogContent>
  );
};

export function Overwrites(props: Props) {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();

  const showMixedIgnoredInfo = hasIgnoredOverrides(props.data.ignored_overrides);

  return (
    <>
      <DialogTitleWithX onClose={props.close}>
        {translate(
          'resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.title',
        )}
      </DialogTitleWithX>
      <Form onSubmit={props.confirmOverwrite}>
        <OverwriteForm {...props} showMixedIgnoredInfo={showMixedIgnoredInfo} />
        <DialogActions>
          <Button
            onClick={props.cancel}
            label="resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.cancel"
            variant="text"
            permissions={{
              resource: `${resourceBasePath}employees/bulk_create`,
              action: 'create',
            }}
            data-testid={`${testId}_CancelButton`}
          />

          <SaveButton
            color="primary"
            permissions={{
              resource: `${resourceBasePath}employees/bulk_create`,
              action: 'create',
            }}
            icon={<></>}
            alwaysEnable
            label="resources.bo_common.b2b.employees.actions.bulk_create.sections.overwrites.cta"
            testId={`${testId}_ConfirmSaveButton`}
          />
        </DialogActions>
      </Form>
    </>
  );
}
